import { EmptyState } from '@arabat/ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { storePath } from '../address.js';
import { DEFAULT_LOOK, ProductGrid } from '../design/sections.js';
import {
  useLocalName,
  useSlug,
  useStorePaths,
  useStoreProducts,
  useStorefront,
  useWishlist,
} from '../shared.js';

/**
 * Every brand of the store (ADR-0146): a logo (or the name) per brand, opening the product list
 * filtered by that brand.
 */
export function BrandsPage() {
  const paths = useStorePaths();
  const { t } = useTranslation();
  const slug = useSlug();
  const store = useStorefront();
  const name = useLocalName();
  const brands = store.data?.brands ?? [];
  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8" data-testid="brands-page">
      <h1 className="mb-6 text-2xl font-bold">{t('brands.title')}</h1>
      {store.isPending ? null : brands.length === 0 ? (
        <EmptyState title={t('brands.none')} />
      ) : (
        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {brands.map((b) => (
            <li key={b.id}>
              <Link
                to={storePath(slug, paths.group(b.id))}
                className="flex h-full flex-col items-center justify-center gap-3 rounded-lg border border-border bg-surface p-4 text-center transition-shadow hover:shadow-md"
              >
                {b.imageUrl ? (
                  <img src={b.imageUrl} alt="" className="h-20 w-full object-contain" />
                ) : null}
                <span className="font-semibold">{name(b)}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

/** The shopper's favourites (ADR-0146): the products they kept with the heart, in this browser. */
export function WishlistPage() {
  const { t } = useTranslation();
  const products = useStoreProducts();
  const { ids } = useWishlist();
  const kept = (products.data?.products ?? []).filter((p) => ids.includes(p.productId));
  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8" data-testid="wishlist-page">
      <h1 className="mb-6 text-2xl font-bold">{t('brands.wishlist')}</h1>
      {products.isPending ? null : kept.length === 0 ? (
        <EmptyState title={t('brands.wishlistEmpty')} />
      ) : (
        <ProductGrid products={kept} columns={4} look={{ ...DEFAULT_LOOK, wishlist: true }} />
      )}
    </main>
  );
}
